import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { getZoomAPI } from '../utils/zoomAPI'

export function TrialBanner() {
  const { authState, user, signInWithZoom, signInWithGoogle } = useAuth()
  const [isStartingTrial, setIsStartingTrial] = useState(false)
  const [showAuthOptions, setShowAuthOptions] = useState(false)

  const handleUpgradeClick = async () => {
    console.log('[TrialBanner] Creating LemonSqueezy checkout...')

    // Get current session tokens
    const { data: { session } } = await supabase.auth.getSession()

    if (!session || !user || !user.email || !user.id) {
      console.error('[TrialBanner] No active session or user')
      alert('Please sign in first')
      return
    }

    console.log('[TrialBanner] Creating checkout for:', user.email)

    try {
      // Create checkout URL directly
      const { createCheckoutUrl } = await import('../lib/lemonsqueezy')
      const checkoutUrl = await createCheckoutUrl('yearly', user.email, user.id, session.access_token)

      console.log('[TrialBanner] Opening LemonSqueezy checkout:', checkoutUrl)

      // Open checkout in browser
      const zoomAPI = getZoomAPI()
      await zoomAPI.openUrl(checkoutUrl)
    } catch (error) {
      console.error('[TrialBanner] Failed to create checkout:', error)
      alert('Failed to open checkout. Please try again.')
    }
  }

  // After successful sign-in, start trial if flag is set
  useEffect(() => {
    const startTrialAfterSignIn = async () => {
      // Check if user just signed in and wants to start trial
      const shouldStartTrial = localStorage.getItem('emdr_start_trial')

      if (shouldStartTrial === 'true' && authState === 'signed-in') {
        localStorage.removeItem('emdr_start_trial')

        try {
          setIsStartingTrial(true)

          // Call start-trial Edge Function
          const { data, error } = await supabase.functions.invoke('start-trial', {
            method: 'POST'
          })

          if (error) {
            console.error('Failed to start trial:', error)
            alert('Failed to start trial. Please try again.')
            return
          }

          console.log('Trial started successfully:', data)

          // Force refresh subscription status
          window.location.reload()
        } catch (error) {
          console.error('Error starting trial:', error)
          alert('Failed to start trial. Please try again.')
        } finally {
          setIsStartingTrial(false)
        }
      }
    }

    startTrialAfterSignIn()
  }, [authState])

  const handleStartWithZoom = async () => {
    try {
      setIsStartingTrial(true)

      // Set flag before OAuth
      localStorage.setItem('emdr_start_trial', 'true')

      // Sign in with Zoom
      await signInWithZoom()

      // After successful sign-in, useEffect above will call start-trial Edge Function
    } catch (error) {
      console.error('Error starting trial with Zoom:', error)
      localStorage.removeItem('emdr_start_trial')
      alert('Failed to start trial. Error: ' + (error instanceof Error ? error.message : String(error)))
      setIsStartingTrial(false)
    }
  }

  const handleStartWithGoogle = async () => {
    try {
      setIsStartingTrial(true)

      // Set flag before OAuth
      localStorage.setItem('emdr_start_trial', 'true')

      // Sign in with Google - opens external browser
      await signInWithGoogle()

      // Notify user about the flow
      alert('A browser window has opened for Google sign-in.\n\n1. Complete sign-in in the browser\n2. Close the browser tab\n3. Return to this Zoom app\n\nYour 14-day free trial will start automatically!')

      // After redirect, useEffect above will call start-trial Edge Function
    } catch (error) {
      console.error('Error starting trial with Google:', error)
      localStorage.removeItem('emdr_start_trial')
      alert('Failed to start trial. Error: ' + (error instanceof Error ? error.message : String(error)))
      setIsStartingTrial(false)
    }
  }

  // Only show for signed-out users (free tier)
  if (authState !== 'signed-out') return null

  // Show auth options
  if (showAuthOptions) {
    return (
      <div style={{
        padding: '16px',
        margin: '16px 0',
        backgroundColor: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: '8px',
      }}>
        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            Start your 14-day free trial
          </h3>
          <button
            onClick={() => setShowAuthOptions(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Zoom OAuth (Recommended) */}
          <button
            onClick={handleStartWithZoom}
            disabled={isStartingTrial}
            style={{
              padding: '12px 16px',
              backgroundColor: '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: isStartingTrial ? 'not-allowed' : 'pointer',
              opacity: isStartingTrial ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>📹</span>
            <span>Sign in with Zoom</span>
            <span style={{ fontSize: '11px', opacity: 0.9 }}>(Recommended)</span>
          </button>

          {/* Google OAuth */}
          <button
            onClick={handleStartWithGoogle}
            disabled={isStartingTrial}
            style={{
              padding: '12px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: isStartingTrial ? 'not-allowed' : 'pointer',
              opacity: isStartingTrial ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>🔐</span>
            <span>Sign in with Google</span>
          </button>
        </div>
      </div>
    )
  }

  // Main banner with call-to-action
  return (
    <div style={{
      padding: '16px',
      margin: '16px 0',
      backgroundColor: '#EFF6FF',
      border: '1px solid #BFDBFE',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <span style={{ fontSize: '24px' }}>🎁</span>
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600 }}>
          14-day free trial
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
          Then 5 sessions/month free — no credit card required!
        </p>
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => setShowAuthOptions(true)}
          disabled={isStartingTrial}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2563EB',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: isStartingTrial ? 'not-allowed' : 'pointer',
            opacity: isStartingTrial ? 0.6 : 1
          }}
        >
          {isStartingTrial ? 'Starting...' : 'Start Free Trial'}
        </button>
        <span style={{ fontSize: '14px', color: '#9CA3AF' }}>or</span>
        <button
          onClick={handleUpgradeClick}
          style={{
            padding: '10px 20px',
            backgroundColor: 'white',
            color: '#2563EB',
            border: '1px solid #2563EB',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Upgrade to Pro
        </button>
      </div>
    </div>
  )
}
